import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Target, Eye, Heart, User, MapPin, Globe } from 'lucide-react';
import { companyInfo, team, serviceImages } from '../mock';

const About = () => {
  return (
    <section id="about" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full0text-sm font-medium mb-4">
            À Propos de SET-G
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
            Notre Expertise & Vision
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Une équipe d'experts passionnés au service de votre transformation digitale
          </p>
        </div>

        {/* Vision & Valeurs */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Vision */}
          <Card className="bg-white border border-slate-200 hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              <CardTitle className="text-xl font-bold text-slate-900">
                Notre Vision
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 leading-relaxed">
                {companyInfo.vision} et être une entreprise avant-gardiste 
                de solutions vertes et durables.
              </p>
            </CardContent>
          </Card>

          {/* Valeurs */}
          <Card className="bg-white border border-slate-200 hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle className="text-xl font-bold text-slate-900">
                Nos Valeurs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {companyInfo.values.map((value, index) => (
                  <Badge 
                    key={index} 
                    variant="secondary" 
                    className="bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                  >
                    {value}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Leadership & Expertise */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Team Description */}
          <div className="space-y-6">
            <h3 className="text-2xl lg:text-3xl font-bold text-slate-900">
              Leadership & Expertise
            </h3>
            
            {/* Team Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl mb-6">
              <img 
                src={serviceImages.teamMeeting}
                alt="Équipe SET-G lors d'une réunion de direction - Audit qualité réseau 5G/4G Télécom à Abidjan"
                title="Réunion SET-G"
                loading="lazy"
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
              <div className="absolute bottom-4 left-6 text-white">
                <h4 className="text-lg font-semibold">Notre Équipe en Action</h4>
                <p className="text-sm opacity-90">Formation et partage d'expertise</p>
              </div>
            </div>
            
            <p className="text-lg text-slate-600 leading-relaxed">
              {team.description}
            </p>
            
            {/* Leader Info */}
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-orange-200">
                    <img 
                      src={team.leader.photo}
                      alt={`${team.leader.name} - ${team.leader.role} de SET-G`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-slate-900">
                      {team.leader.name}
                    </CardTitle>
                    <CardDescription className="text-orange-600 font-medium">
                      {team.leader.role}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Qualifications :</h4>
                    <ul className="space-y-1">
                      {team.leader.qualifications.map((qual, index) => (
                        <li key={index} className="text-slate-600 flex items-center space-x-2">
                          <span className="w-2 h-2 bg-orange-600 rounded-full"></span>
                          <span>{qual}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="border-t pt-4 space-y-3">
                    <p className="text-slate-600 italic">
                      {team.leader.experience}
                    </p>
                    <p className="text-slate-600 leading-relaxed">
                      {team.leader.background}
                    </p>
                    <div className="flex items-center space-x-2 text-sm">
                      <Globe className="w-4 h-4 text-orange-600" />
                      <span className="text-slate-600 font-medium">{team.leader.internationalExperience}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Company Highlights */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
              <h3 className="text-xl font-bold text-slate-900 mb-6">
                Nos Forces
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Experience Internationale</h4>
                    <p className="text-slate-600">Projets de conception, planification et déploiement à l'international</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Expertise Locale</h4>
                    <p className="text-slate-600">Connaissance approfondie du marché ivoirien et africain</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Solutions Vertes</h4>
                    <p className="text-slate-600">Approche durable et respectueuse de l'environnement</p>
                  </div>
                </div>
              </div>
            
              {/* Achievements réels */}
              <div className="bg-slate-900 rounded-2xl p-6 text-center">
                <h4 className="text-lg font-bold text-white mb-4">
                  Expertise Reconnue
                </h4>
                <p className="text-slate-300 mb-4">
                  Solutions éprouvées et expertise reconnue par les régulateurs
                </p>
                <div className="klex justify-center space-x-4 text-sm text-slate-400">
                  <span>• Partenaire ARTCI</span>
                  <span>• Expert Audit QoS</span>
                  <span>• Ingénierie Radio</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
   );
};

export default About;